import { type LigneMarge, margesMensuelles, type MoisMarge } from '@imp/shared/marge-mensuelle';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { type AlertesResult, computeAlertes, PRIORITE_META } from '@/modules/alertes/api';
import { useAuth } from '@/modules/auth/AuthContext';
import { formatEuro } from '@/modules/catalogue/api';
import { employeNom, listChantiersPilotage, listEmployes } from '@/modules/chantiers/api';
import { markDevisRelance } from '@/modules/devis/api';
import { listFacturesForPilotage } from '@/modules/factures/api';
import { MargeMoisCard } from '@/modules/pilotage/MargeMoisCard';

function eur(n: number): string {
  return formatEuro(n);
}

export function PilotagePage() {
  const queryClient = useQueryClient();
  const { profil } = useAuth();
  const alertesQ = useQuery<AlertesResult>({ queryKey: ['alertes'], queryFn: computeAlertes });
  const chantiersQ = useQuery({ queryKey: ['chantiers-pilotage'], queryFn: listChantiersPilotage });
  const facturesQ = useQuery({ queryKey: ['factures-pilotage'], queryFn: listFacturesForPilotage });
  const employesQ = useQuery({ queryKey: ['employes'], queryFn: listEmployes });

  const relanceMut = useMutation({
    mutationFn: (id: string) => markDevisRelance(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['alertes'] });
    },
  });

  const empMap = new Map((employesQ.data ?? []).map((e) => [e.id, employeNom(e)]));
  const suivi = (alertesQ.data?.alertes ?? []).filter((a) => a.entite === 'devis');

  // Toute la marge se calcule dans @imp/shared/marge-mensuelle, pur et testé :
  // ici on ne fait que donner les lignes lues et afficher ce qui en revient.
  const { mois, sansMois } = margesMensuelles({
    chantiers: (chantiersQ.data ?? []).map((ch) => ({
      id: ch.id,
      numero: ch.numero,
      libelle: ch.libelle,
      statut: ch.statut,
      chefChantierId: ch.chef_chantier_id,
      commercialId: ch.metadata?.commercial_id ?? null,
      achats: ch.metadata?.montant_achats ?? 0,
      montantVenteHt: ch.metadata?.montant_vente_ht ?? 0,
      datePrevueFin: ch.date_prevue_fin,
      datePrevueDebut: ch.date_prevue_debut,
    })),
    factures: (facturesQ.data ?? []).flatMap((f) => {
      const chantierId = f.chantier_id;
      // Une facture sans chantier ne dit rien d'une marge de chantier.
      if (chantierId === null) return [];
      return [
        {
          id: f.id,
          chantierId,
          kind: f.kind,
          statut: f.statut,
          montantHt: Number(f.montant_ht),
          dateEmission: f.date_emission,
          factureLieeId: f.facture_liee_id,
        },
      ];
    }),
  });

  // Le mois le plus récent s'ouvre de lui-même : c'est celui qu'on vient voir.
  const [moisChoisi, setMoisChoisi] = useState<string | null>(null);
  const moisCourant: MoisMarge | undefined = mois.find((m) => m.cle === moisChoisi) ?? mois[0];
  const lignes = moisCourant?.lignes ?? [];

  const nomChef = (l: LigneMarge) =>
    l.chefChantierId === null ? 'Sans chef' : (empMap.get(l.chefChantierId) ?? '—');
  const nomCommercial = (l: LigneMarge) =>
    l.commercialId === null ? 'Sans commercial' : (empMap.get(l.commercialId) ?? '—');

  const aggregate = (sel: (l: LigneMarge) => string) => {
    const m = new Map<string, { ca: number; marge: number }>();
    for (const l of lignes) {
      const k = sel(l);
      const cur = m.get(k) ?? { ca: 0, marge: 0 };
      cur.ca += l.caHt;
      cur.marge += l.marge;
      m.set(k, cur);
    }
    return [...m.entries()].sort((a, b) => b[1].ca - a[1].ca);
  };
  const parChef = aggregate(nomChef);
  const parCommercial = aggregate(nomCommercial);

  const margeClass = (m: number) => (m < 0 ? 'text-red-600' : 'text-green-600');

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Pilotage</h1>
          <p className="text-muted-foreground">Suivi commercial et rentabilité des chantiers.</p>
        </div>
        {profil?.isAdmin ? (
          <Button asChild variant="outline" size="sm">
            <Link to="/pilotage/tresorerie">
              Trésorerie &amp; ventes par mois <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        ) : null}
      </div>

      {/* ---------- Suivi client ---------- */}
      <Card>
        <CardHeader>
          <CardTitle>Suivi client — relances commerciales</CardTitle>
        </CardHeader>
        <CardContent>
          {alertesQ.isLoading ? (
            <p className="text-muted-foreground text-sm">Analyse…</p>
          ) : suivi.length === 0 ? (
            <p className="text-muted-foreground text-sm">Aucun client à relancer pour le moment.</p>
          ) : (
            <div className="divide-y">
              {suivi.map((a) => {
                const meta = PRIORITE_META[a.priorite];
                const relancable = a.type === 'devis_sans_reponse' || a.type === 'devis_expire';
                return (
                  <div
                    key={a.id}
                    className="flex flex-wrap items-center justify-between gap-3 py-2.5"
                  >
                    <div className="flex items-center gap-3">
                      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${meta.dot}`} />
                      <div>
                        <div className="text-sm font-medium">{a.titre}</div>
                        <div className="text-muted-foreground text-xs">{a.description}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {a.montant !== undefined && a.montant > 0 ? (
                        <span className="text-sm font-semibold tabular-nums">{eur(a.montant)}</span>
                      ) : null}
                      {relancable ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={relanceMut.isPending}
                          onClick={() => {
                            relanceMut.mutate(a.entiteId);
                          }}
                        >
                          Marquer relancé
                        </Button>
                      ) : null}
                      <Button asChild size="sm" variant="ghost">
                        <Link to={a.lien}>
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <MargeMoisCard
        mois={mois}
        moisCourant={moisCourant}
        sansMois={sansMois}
        chargement={chantiersQ.isLoading}
        nomChef={nomChef}
        onChangerMois={setMoisChoisi}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        {[
          { titre: `CA par chef — ${moisCourant?.label ?? ''}`, data: parChef },
          { titre: `CA par commercial — ${moisCourant?.label ?? ''}`, data: parCommercial },
        ].map((bloc) => (
          <Card key={bloc.titre} className="overflow-hidden">
            <CardHeader>
              <CardTitle>{bloc.titre}</CardTitle>
            </CardHeader>
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground border-y text-left text-xs uppercase">
                <tr>
                  <th className="px-4 py-2 font-medium">Équipe</th>
                  <th className="px-4 py-2 text-right font-medium">CA HT</th>
                  <th className="px-4 py-2 text-right font-medium">Marge</th>
                </tr>
              </thead>
              <tbody>
                {bloc.data.map(([nom, v]) => (
                  <tr key={nom} className="border-b last:border-0">
                    <td className="px-4 py-2">{nom}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{eur(v.ca)}</td>
                    <td className={`px-4 py-2 text-right tabular-nums ${margeClass(v.marge)}`}>
                      {eur(v.marge)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        ))}
      </div>
    </div>
  );
}
